/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { Grid, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { UseForm } from "../../types/UseForm";
import _ from "lodash";
import ValidatorNumberField from "../Validator/ValidatorNumberField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import ValidatorSelect from "../Validator/ValidatorSelect";

type AlimonyExpensesProps = {
  path: string;
  formValue: any;
  onChange: UseForm["handleChange"];
  disabled: boolean;
};

type Item = { key: string; category: string; name: string; monthlyAmount: string };

export default function AlimonyExpenses({ path, formValue, onChange, disabled }: AlimonyExpensesProps) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  useEffect(() => {
    if (disabled && confirmKey) {
      setConfirmKey(null);
    }
  }, [disabled, confirmKey]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ target: { name: e.target.name, value: e.target.value } });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    onChange({ target: { name: e.target.name as string, value: e.target.value } });
  };

  const key = () => Math.random().toString(36).slice(2, 11);
  const addPosition = () => {
    onChange({
      target: {
        name: path,
        value: [
          ...formValue,
          {
            key: key(),
            category: "other",
            name: "",
            monthlyAmount: "",
          },
        ],
      },
    });
  };

  const removePosition = (k: string) => {
    onChange({
      target: {
        name: path,
        value: formValue.filter((item: Item) => item.key !== k),
      },
    });
  };

  const confirmDelete = (k: string) => {
    if (confirmKey === k) {
      setConfirmKey(null);
      removePosition(k);
      return;
    }

    setConfirmKey(k);
  };

  return (
    <Grid container spacing={3}>
      <Grid
        sx={{
          textAlign: "center"
        }}
        size={12}>
        <Typography variant={"h3"}>Ausgaben</Typography>
      </Grid>
      {_.map(formValue, (value, index: number) => (
        <React.Fragment key={value.key}>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <ValidatorSelect
              label={`Kategorie ${index + 1}`}
              name={`${path}[${index}].category`}
              value={value.category}
              onChange={handleSelectChange}
              disabled={disabled}
            >
              <MenuItem value={"debtRepayment"}>Tilgung von Schulden</MenuItem>
              <MenuItem value={"insurance"}>Versicherung</MenuItem>
              <MenuItem value={"privatePension"}>Private Altersvorsorge</MenuItem>
              <MenuItem value={"visitationCosts"}>Kosten für Umgangsrecht</MenuItem>
              <MenuItem value={"other"}>Sonstiges</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 5
            }}>
            <TextField
              name={`${path}[${index}].name`}
              label={`Bezeichnung ${index + 1}`}
              value={value.name}
              onChange={handleChange}
              disabled={disabled}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 2
            }}>
            <ValidatorNumberField
              name={`${path}[${index}].monthlyAmount`}
              value={value.monthlyAmount}
              label={`Monatlicher Betrag ${index + 1}`}
              onChange={handleChange}
              fieldType={"money"}
              numberType={"float"}
              disabled={disabled}
            />
          </Grid>
          <Grid
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            size={{
              xs: 12,
              md: 2
            }}>
            <Button
              variant="contained"
              color={confirmKey === value.key ? "error" : "primary"}
              onClick={() => confirmDelete(value.key)}
              disabled={disabled}
            >
              {confirmKey === value.key ? "Sicher?" : "entfernen"}
            </Button>
          </Grid>
        </React.Fragment>
      ))}
      <Grid
        sx={{
          textAlign: "center"
        }}
        size={12}>
        <Button variant={"contained"} color={"primary"} onClick={addPosition}>
          Position hinzufügen
        </Button>
      </Grid>
    </Grid>
  );
}
